import { useDefaultPagination } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail/hooks/useInvoicesTransaction';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { FC, memo, useMemo } from 'react';
import { useColumns } from './Columns/columns';
import { TransactionsDetailTable } from './TransactionsDetailTable';

export type LoadingTransactionsDetailTableProps = {
  /**
   * Render the column header over the skeleton. Opt-in because the legacy
   * call site is shared with the Application and Accounting drawers.
   */
  showHeader?: boolean;
};

export const LoadingTransactionsDetailTable: FC<LoadingTransactionsDetailTableProps> =
  memo(({ showHeader = false }) => {
    const isOnline = useNetworkStatus();
    const pagination = useDefaultPagination();
    // The dynamic set needs `useGetTransactionProperties`, which suspends, so
    // the defaults are all a Suspense fallback can show. Only column
    // metadata (width, header) is used here — no real cell data is
    // rendered, so `alwaysShowTooltip` has no visible effect on the
    // skeleton either way.
    const defaultColumns = useColumns(true, true);

    const loadingColumns = useMemo(() => {
      if (!showHeader) return [];

      // `SkeltonTable` forwards `columns` but not `scroll`, so the loading
      // table is `table-layout: auto` capped at the container width. Two
      // consequences: `fixed` has nothing to pin against and crushes every
      // column, and unfloored CJK labels collapse to ~1 char and wrap. Floor
      // each cell at its declared width to match the loaded table.
      return defaultColumns.map((column) => ({
        ...column,
        fixed: undefined,
        onHeaderCell: () => ({ style: { minWidth: column.width } }),
      }));
    }, [showHeader, defaultColumns]);

    const tableProps = useMemo(
      () => ({ data: [], columns: loadingColumns, loading: isOnline }),
      [isOnline, loadingColumns]
    );

    return (
      <TransactionsDetailTable
        table={tableProps}
        pagination={pagination}
        isSearchEmpty={false}
      />
    );
  });
LoadingTransactionsDetailTable.displayName = 'LoadingTransactionsDetailTable';
